import React from 'react';
import {
  Calendar,
  Users,
  Music,
  Trophy,
  Video,
  Clock,
  ChevronRight,
  Star,
  DollarSign
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { useTheme } from '../../context/ThemeContext';

const UserDashboard = () => {
  const { theme } = useTheme();
  const isDarkMode = theme === "dark";
  
  // Example data - replace with real data from your backend
  const upcomingEvents = [
    {
      id: 1,
      name: "Spring Showcase 2025",
      date: "March 15, 2025",
      location: "Atlanta, GA",
      type: "Competition",
      registeredRoutines: 3
    },
    {
      id: 2,
      name: "Summer Dance Convention",
      date: "June 22, 2025",
      location: "Orlando, FL",
      type: "Convention + Competition",
      registeredRoutines: 2
    }
  ];

  const recentCritiques = [
    {
      id: 1,
      routineName: "Starlight Symphony",
      event: "Winter Nationals 2024",
      date: "December 12, 2024",
      score: 285.5,
      placement: "1st Overall"
    },
    {
      id: 2,
      routineName: "Urban Dreams",
      event: "Winter Nationals 2024",
      date: "December 12, 2024",
      score: 278.2,
      placement: "3rd Overall"
    }
  ];

  return (
    <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8">
      <div className="space-y-6 pt-10 md:pt-0">
      {/* Header */}
      <div>
        <h1 className={`text-2xl md:text-3xl font-bold mb-2 ${isDarkMode ? "text-white" : "text-gray-900"}`}>Welcome Back</h1>
        <p className={`text-sm md:text-base ${isDarkMode ? "text-gray-400" : "text-gray-600"}`}>
          Here's what's happening with your studio
        </p>
      </div>

      {/* Stats Overview */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className={`rounded-lg p-6 shadow-lg ${
          isDarkMode 
            ? "bg-[#1a1f2d]/80 backdrop-blur-sm border border-white/5" 
            : "bg-base-100 border border-base-200"
        }`}>
          <div className="flex items-center">
            <div className="p-2 bg-primary/10 rounded-lg">
              <Music className="h-6 w-6 text-primary" />
            </div>
            <div className="ml-4">
              <p className={`text-sm ${isDarkMode ? "text-gray-400" : "text-base-content/70"}`}>Active Routines</p>
              <p className={`text-2xl font-semibold ${isDarkMode ? "text-white" : "text-base-content"}`}>12</p>
            </div>
          </div>
        </div>

        <div className={`rounded-lg p-6 shadow-lg ${
          isDarkMode 
            ? "bg-[#1a1f2d]/80 backdrop-blur-sm border border-white/5" 
            : "bg-base-100 border border-base-200"
        }`}>
          <div className="flex items-center">
            <div className="p-2 bg-success/10 rounded-lg">
              <Users className="h-6 w-6 text-success" />
            </div>
            <div className="ml-4">
              <p className={`text-sm ${isDarkMode ? "text-gray-400" : "text-base-content/70"}`}>Registered Dancers</p>
              <p className={`text-2xl font-semibold ${isDarkMode ? "text-white" : "text-base-content"}`}>48</p>
            </div>
          </div>
        </div>

        <div className={`rounded-lg p-6 shadow-lg ${
          isDarkMode 
            ? "bg-[#1a1f2d]/80 backdrop-blur-sm border border-white/5" 
            : "bg-base-100 border border-base-200"
        }`}>
          <div className="flex items-center">
            <div className="p-2 bg-warning/10 rounded-lg">
              <Trophy className="h-6 w-6 text-warning" />
            </div>
            <div className="ml-4">
              <p className={`text-sm ${isDarkMode ? "text-gray-400" : "text-base-content/70"}`}>Competition Entries</p>
              <p className={`text-2xl font-semibold ${isDarkMode ? "text-white" : "text-base-content"}`}>8</p>
            </div>
          </div>
        </div>

        <div className={`rounded-lg p-6 shadow-lg ${
          isDarkMode 
            ? "bg-[#1a1f2d]/80 backdrop-blur-sm border border-white/5" 
            : "bg-base-100 border border-base-200"
        }`}>
          <div className="flex items-center">
            <div className="p-2 bg-secondary/10 rounded-lg">
              <Star className="h-6 w-6 text-secondary" />
            </div>
            <div className="ml-4">
              <p className={`text-sm ${isDarkMode ? "text-gray-400" : "text-base-content/70"}`}>Scholarships Won</p>
              <p className={`text-2xl font-semibold ${isDarkMode ? "text-white" : "text-base-content"}`}>5</p>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Upcoming Events */}
        <div className={`rounded-lg shadow-lg ${
          isDarkMode 
            ? "bg-[#1a1f2d]/80 backdrop-blur-sm border border-white/5" 
            : "bg-base-100 border border-base-200"
        }`}>
          <div className={`p-6 border-b ${isDarkMode ? "border-white/10" : "border-base-200"}`}>
            <div className="flex justify-between items-center">
              <h2 className={`text-lg font-semibold ${isDarkMode ? "text-white" : "text-base-content"}`}>Upcoming Events</h2>
              <Link 
                to="/user/events"
                className="text-sm text-primary hover:text-primary/80 flex items-center"
              >
                View All
                <ChevronRight className="h-4 w-4 ml-1" />
              </Link>
            </div>
          </div>
          <div className="p-4">
            <div className="space-y-4">
              {upcomingEvents.map(event => (
                <div 
                  key={event.id}
                  className={`flex items-center p-4 rounded-lg transition-colors ${
                    isDarkMode 
                      ? "bg-[#252D42]/50 hover:bg-[#252D42]" 
                      : "bg-base-200 hover:bg-base-300"
                  }`}
                >
                  <div className="p-3 bg-primary/10 rounded-lg">
                    <Calendar className="h-6 w-6 text-primary" />
                  </div>
                  <div className="ml-4 flex-1">
                    <h3 className={`font-medium ${isDarkMode ? "text-white" : "text-base-content"}`}>{event.name}</h3>
                    <p className={`text-sm ${isDarkMode ? "text-gray-400" : "text-base-content/70"}`}>
                      {event.date} • {event.location}
                    </p>
                  </div>
                  <div className="text-right">
                    <span className="px-3 py-1 bg-primary/10 text-primary text-sm rounded-full">
                      {event.registeredRoutines} Routines
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Recent Critiques */}
        <div className={`rounded-lg shadow-lg ${
          isDarkMode 
            ? "bg-[#1a1f2d]/80 backdrop-blur-sm border border-white/5" 
            : "bg-base-100 border border-base-200"
        }`}>
          <div className={`p-6 border-b ${isDarkMode ? "border-white/10" : "border-base-200"}`}>
            <div className="flex justify-between items-center">
              <h2 className={`text-lg font-semibold ${isDarkMode ? "text-white" : "text-base-content"}`}>Recent Critiques</h2>
              <Link 
                to="/user/critiques"
                className="text-sm text-primary hover:text-primary/80 flex items-center"
              >
                View All
                <ChevronRight className="h-4 w-4 ml-1" />
              </Link>
            </div>
          </div>
          <div className="p-4">
            <div className="space-y-4">
              {recentCritiques.map(critique => (
                <div 
                  key={critique.id}
                  className={`flex items-center p-4 rounded-lg transition-colors ${
                    isDarkMode 
                      ? "bg-[#252D42]/50 hover:bg-[#252D42]" 
                      : "bg-base-200 hover:bg-base-300"
                  }`}
                >
                  <div className="p-3 bg-success/10 rounded-lg">
                    <Video className="h-6 w-6 text-success" />
                  </div>
                  <div className="ml-4 flex-1">
                    <h3 className={`font-medium ${isDarkMode ? "text-white" : "text-base-content"}`}>{critique.routineName}</h3>
                    <p className={`text-sm ${isDarkMode ? "text-gray-400" : "text-base-content/70"}`}>
                      {critique.event} • {critique.date}
                    </p>
                  </div>
                  <div className="text-right">
                    <div className={`text-lg font-semibold ${isDarkMode ? "text-white" : "text-base-content"}`}>{critique.score}</div>
                    <div className="text-sm text-success">{critique.placement}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Quick Actions */}
        <div className={`rounded-lg shadow-lg lg:col-span-2 ${
          isDarkMode 
            ? "bg-[#1a1f2d]/80 backdrop-blur-sm border border-white/5" 
            : "bg-base-100 border border-base-200"
        }`}>
          <div className={`p-6 border-b ${isDarkMode ? "border-white/10" : "border-base-200"}`}>
            <h2 className={`text-lg font-semibold ${isDarkMode ? "text-white" : "text-base-content"}`}>Quick Actions</h2>
          </div>
          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Link
                to="/user/routines?action=new"
                className={`p-4 rounded-lg transition-colors flex items-center ${
                  isDarkMode 
                    ? "bg-[#252D42]/50 hover:bg-[#252D42]" 
                    : "bg-base-200 hover:bg-base-300"
                }`}
              >
                <Music className="h-5 w-5 text-primary mr-3" />
                <span className={isDarkMode ? "text-white" : "text-base-content"}>Create New Routine</span>
              </Link>
              
              <Link
                to="/user/events"
                className={`p-4 rounded-lg transition-colors flex items-center ${
                  isDarkMode 
                    ? "bg-[#252D42]/50 hover:bg-[#252D42]" 
                    : "bg-base-200 hover:bg-base-300"
                }`}
              >
                <Calendar className="h-5 w-5 text-primary mr-3" />
                <span className={isDarkMode ? "text-white" : "text-base-content"}>Register for Event</span>
              </Link>
              
              <Link
                to="/user/studio"
                className={`p-4 rounded-lg transition-colors flex items-center ${
                  isDarkMode 
                    ? "bg-[#252D42]/50 hover:bg-[#252D42]" 
                    : "bg-base-200 hover:bg-base-300"
                }`}
              >
                <Users className="h-5 w-5 text-primary mr-3" />
                <span className={isDarkMode ? "text-white" : "text-base-content"}>Manage Dancers</span>
              </Link>
            </div>
          </div>
        </div>
      </div>
      </div>
    </div>
  );
};

export default UserDashboard;